import { z } from 'zod'
import type { Channel } from './enums'

/**
 * Platform operations (design 16-17, 19, 22 #4): the company lifecycle, sign-up and onboarding,
 * sign-in policy, billing and credits, budgets, service levels, the audit log and the operator console.
 */

// ---------------------------------------------------------------------------
// Company lifecycle (OP-07)
// ---------------------------------------------------------------------------
export const CompanyStatus = z.enum(['onboarding', 'shadow', 'active', 'suspended'])
export type CompanyStatus = z.infer<typeof CompanyStatus>

export interface LifecycleView {
  status: CompanyStatus
  reason: string | null
  changed_at: string | null
  go_live_at: string | null
  /** Where an admin can move the company next from here (operators can also suspend and restore). */
  next: CompanyStatus[]
}

export interface OnboardingStep {
  key: 'profile' | 'channel' | 'email_setup' | 'knowledge' | 'house_rules' | 'team' | 'security' | 'billing' | 'actions' | 'shadow' | 'go_live'
  label: string
  done: boolean
  detail: string
  /** Console path that completes the step. */
  link: string
  /** Required before going live. */
  required: boolean
}
export interface OnboardingView { lifecycle: LifecycleView; steps: OnboardingStep[]; ready_to_go_live: boolean; blockers: string[] }

export const LifecycleInput = z.object({ to: z.enum(['shadow', 'active']), reason: z.string().max(300).optional() })

// ---------------------------------------------------------------------------
// Self-serve sign-up (OP-08)
// ---------------------------------------------------------------------------
export const SLUG = /^[a-z][a-z0-9-]{2,30}[a-z0-9]$/
export const RESERVED_SLUGS = ['www', 'api', 'app', 'admin', 'ops', 'console', 'mail', 'status', 'help', 'support', 'signup', 'login', 'au', 'uk', 'eu', 'na', 'chat', 'preview', 'router', 'control']

/**
 * The industry packs (I 20-P4-02). The packs themselves are in core (packs.ts, packsMore.ts), typed by these keys,
 * so a pack added there without its key here (or the other way round) does not compile.
 */
export const PACK_KEYS = ['leisure', 'appointments', 'service_plans', 'agency', 'trades', 'retail', 'hospitality', 'fitness', 'property', 'professional'] as const
export type PackKey = (typeof PACK_KEYS)[number]

export const SignupInput = z.object({
  company_name: z.string().trim().min(2).max(80),
  slug: z.string().trim().toLowerCase().regex(SLUG, 'Use 4 to 32 lowercase letters, numbers and hyphens, starting with a letter'),
  region: z.enum(['au', 'uk', 'eu', 'na']),
  pack: z.enum(PACK_KEYS).nullable(),
  plan: z.string().min(1).max(40),
  admin_name: z.string().trim().min(1).max(80),
  admin_email: z.email(),
  timezone: z.string().min(3).max(60).optional(),
  accept_terms: z.literal(true),
  /** Cloudflare Turnstile token, when the cell requires one. */
  captcha: z.string().max(4000).optional(),
})
export interface SignupOptions {
  regions: { code: string; name: string; status: 'planned' | 'live' | 'draining'; this_cell: boolean }[]
  plans: PlanView[]
  packs: { key: string; name: string; description: string }[]
  captcha_site_key: string | null
}
export interface SignupResult { company_id: string; slug: string; console_url: string; invite_sent: boolean; detail: string }

// ---------------------------------------------------------------------------
// Sign-in policy (17.1-01/02): single sign-on and multi-factor login
// ---------------------------------------------------------------------------
export const SsoProvider = z.enum(['google', 'azure', 'saml'])
export type SsoProvider = z.infer<typeof SsoProvider>
export interface AuthPolicyView {
  mfa_required: boolean
  sso_required: boolean
  sso_providers: SsoProvider[]
  saml_domain: string | null
  saml_provider_id: string | null
  /** Whether the policy was set by an admin or is the platform default. */
  source: 'company' | 'default'
  updated_at: string | null
}
export const AuthPolicyInput = z.object({
  mfa_required: z.boolean(),
  sso_required: z.boolean(),
  sso_providers: z.array(SsoProvider).max(3),
  saml_domain: z.string().trim().toLowerCase().regex(/^[a-z0-9.-]+\.[a-z]{2,}$/).nullable().optional(),
  saml_provider_id: z.string().uuid().nullable().optional(),
})
/** Public: what the login page offers for a company (no secrets). */
export interface LoginOptions { company: { slug: string; name: string; logo_url?: string | null; color?: string | null } | null; sso_providers: SsoProvider[]; saml_domain: string | null; password_allowed: boolean }
/** Error codes the API returns when a sign-in does not meet the company's policy. */
export type AuthPolicyError = 'mfa_required' | 'mfa_enrol_required' | 'sso_required'

export interface CompanyMembership { id: string; slug: string; name: string; role: string }

/** Extra fields on GET /me: the lifecycle (for the onboarding banner), billing state, and the sign-in's assurance. */
export interface MeExtras {
  lifecycle: { status: CompanyStatus; go_live_at: string | null }
  billing_status: BillingStatus | null
  /** How many companies this sign-in has a seat in (the switcher shows when above 1). */
  memberships: number
  auth: { aal: 'aal1' | 'aal2' | null; method: string | null }
}

// ---------------------------------------------------------------------------
// Billing (OP-04/05), credits (OP-06), usage (OP-09) and budgets (OP-10)
// ---------------------------------------------------------------------------
export interface PlanView {
  key: string; name: string; description: string | null; currency: string
  monthly_minor: number; per_resolved_minor: number; included_credits: number; voice_minute_minor: number
  /** Whether Stripe prices are configured for it (it can be bought online). */
  purchasable: boolean
}
export interface CreditPackView { key: string; name: string; credits: number; price_minor: number; currency: string; purchasable: boolean }
export type BillingStatus = 'none' | 'incomplete' | 'trialing' | 'active' | 'past_due' | 'unpaid' | 'canceled'
export interface BillingInvoiceView {
  id: string; number: string | null; status: string; currency: string; amount_due_minor: number; amount_paid_minor: number
  period_start: string | null; period_end: string | null; hosted_url: string | null; pdf_url: string | null; created_at: string
}
export interface CreditEntryView { id: string; delta: number; kind: 'grant' | 'purchase' | 'spend' | 'refund' | 'adjustment' | 'expiry'; reason: string; created_at: string }
export interface UsageLine { metric: 'resolved_conversations' | 'voice_seconds' | 'ai_cost_micros' | 'tasks_done'; label: string; quantity: number; unit: string; amount_minor: number | null }
export interface BudgetView { kind: 'ai_month' | 'voice_month' | 'task_month'; label: string; limit_micros: number | null; used_micros: number; used_fraction: number | null; state: 'ok' | 'warning' | 'exceeded' | 'unset' }
export interface BillingView {
  configured: boolean
  status: BillingStatus
  plan: PlanView | null
  plans: PlanView[]
  period: { start: string | null; end: string | null }
  grace_until: string | null
  usage: UsageLine[]
  estimate_minor: number | null
  currency: string
  credits: { balance: number; included_monthly: number; ledger: CreditEntryView[] }
  packs: CreditPackView[]
  invoices: BillingInvoiceView[]
  budgets: BudgetView[]
}
export const CheckoutInput = z.object({ plan: z.string().min(1).max(40) })
export const CreditPurchaseInput = z.object({ pack: z.string().min(1).max(40) })
export const BudgetLimitInput = z.object({ kind: z.enum(['ai_month', 'voice_month', 'task_month']), limit_micros: z.number().int().nonnegative().nullable() })

// ---------------------------------------------------------------------------
// Audit log (16.4-12, 17.1-05)
// ---------------------------------------------------------------------------
export interface AuditEntryView {
  id: string; at: string; actor_kind: string; actor: string; action: string; target_kind: string | null; target_id: string | null; data: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// Today extras (18.1) and analytics extras (18.11, 19, 19.1)
// ---------------------------------------------------------------------------
export interface TodayHour { hour: string; ai_alone: number; ai_approved: number; person: number }
export interface TodayExtras {
  hourly: TodayHour[]
  by_channel: { channel: Channel; conversations: number; first_reply_s: number | null }[]
  company_tz: string
  lifecycle: { status: CompanyStatus; go_live_at: string | null }
  /** Every alarm still open or acknowledged (Today lists at most ten; the rest are on /alarms). */
  alarms_total?: number
  /** Alarms that closed in the last 7 days ("auto-resolved in the last 7 days"). */
  alarms_resolved_7d?: number
  /** The background steps' health, for admins only (null for other roles). */
  engine?: EngineStepView[] | null
  /** Open alarms about failing background jobs and failed sends (details in Analytics, Volume and cost). */
  errors?: { jobs: number; sends: number }
}
/** An alarm on the tenant Alarms page (GET /alarms). */
export interface TenantAlarmView {
  id: string; kind: string; subject: string; message: string; severity: string; status: 'open' | 'acknowledged' | 'resolved'
  opened_at: string; acknowledged_at: string | null; acknowledged_by: string | null; resolved_at: string | null
  /** The conversation the subject belongs to (a `conversation:` or `message:` subject), to open it in one click. */
  conversation_id: string | null
  /** Where the finding lives (alarm.detail.url: the conversation, person or desk item), when the check set one. */
  url: string | null
  /** Since when, how many, and example items to look at (outcome and intake findings). */
  detail?: { since?: string | null; affected?: number; examples?: { kind: string; id: string; url: string; label?: string }[] }
}

export interface MeasureTarget { label: string; met: boolean | null }
export interface AnalyticsMarker { at: string; week: string; kind: 'go_live' | 'release' | 'autonomy' | 'plan' | 'lifecycle'; label: string }
export interface CostBreakdown {
  currency: string
  per_resolved_minor: number | null
  total_minor: number
  lines: { key: 'ai_models' | 'classification' | 'voice' | 'sms' | 'email' | 'sandboxes'; label: string; minor: number; estimated: boolean }[]
  fx: { usd_rate: number; as_of: string | null }
}
export interface ServiceLevel {
  key: 'uptime' | 'ring_to_answer' | 'chat_first_response' | 'email_first_reply' | 'silence_detection'
  label: string; target: string; value: number | null; unit: '%' | 's' | 'min'; p95: number | null; within_target: number | null; samples: number; definition: string
}
export interface AnalyticsExtras {
  go_live_at: string | null
  /** The same measures in the first 28 days after go-live, for "change since go-live". */
  baseline: Record<string, number | null>
  markers: AnalyticsMarker[]
  cost: CostBreakdown
  service_levels: ServiceLevel[]
  weekly_by_channel: { week: string; channel: string; conversations: number; resolved_by_ai: number }[]
  escalation_targets: { question_key: string; precision_target: number; recall_target: number }[]
}

// ---------------------------------------------------------------------------
// Operator console (16.3-11, 4-07, OP-11)
// ---------------------------------------------------------------------------
export interface OperatorView { id: string; email: string; name: string; role: 'viewer' | 'support' | 'admin' }
export interface GrantView {
  id: string; operator: string; region: string; company_id: string | null; company_name: string | null
  reason: string; access: 'read'; starts_at: string; expires_at: string; revoked_at: string | null; active: boolean
}
export const GrantInput = z.object({
  region: z.enum(['au', 'uk', 'eu', 'na']),
  company_id: z.string().uuid().nullable(),
  reason: z.string().trim().min(10).max(500),
  minutes: z.number().int().min(5).max(720).default(60),
})
export interface OpsCompanyRow {
  id: string; slug: string; name: string; region: string; status: CompanyStatus; plan_key: string | null; billing_status: BillingStatus
  created_at: string; go_live_at: string | null
  credits: number
  usage_30d: { resolved_conversations: number; voice_seconds: number; ai_cost_micros: number; tasks_done: number }
  /** Only for companies in this cell: open alarms (counts, no content). */
  alarms: { critical: number; warning: number } | null
  silence: number | null
}
export interface OpsRegionHealth {
  code: string; name: string; status: 'planned' | 'live' | 'draining'; cell_url: string; registered_at: string | null
  health: { ok: boolean; detail: string; checked_at: string } | null
  uptime_30d: number | null
}
export interface OpsOverview {
  operator: OperatorView
  region: string
  regions: OpsRegionHealth[]
  companies: OpsCompanyRow[]
  incidents: IncidentView[]
  grants: GrantView[]
  totals: { companies: number; active: number; suspended: number; ai_cost_micros_30d: number; resolved_30d: number }
  /** The platform and this region's kill switches (control plane), so operators can see and press them. */
  switches?: OpsSwitchesView
}

// ---------------------------------------------------------------------------- engine (scheduled steps) and kill switches
/** One scheduled step (a sweep step, or a daily or weekly job): its switch and its last run (the worker health table). */
export interface EngineStepView {
  name: string; label: string; critical: boolean; dry_run_kind: 'native' | 'log'
  mode: 'live' | 'dry_run' | 'off'; reason: string | null; source: 'env' | 'console' | 'default'; set_by: string | null; set_at: string | null
  last_run: string | null; last_ok: string | null; ok: boolean | null; error: string | null; failures: number
  last_error: { at: string; error: string | null } | null
  /** What a dry run would have done on its last run. */
  decisions: unknown[] | null
  sub_steps: { name: string; ok: boolean; error: string | null; failures: number; last_run: string }[]
  /** Health in one word (Today's engine strip): failing 3 runs in a row, not run for 10 minutes, retrying, switched off, or fine. */
  health?: 'ok' | 'retrying' | 'failing' | 'overdue' | 'off'
}
export interface EngineView { region: string; sweep_at: string | null; daily_at: string | null; weekly_at: string | null; steps: EngineStepView[]; jobs: EngineStepView[] }
export const StepControlInput = z.object({ mode: z.enum(['live', 'dry_run', 'off']), reason: z.string().trim().max(300).nullish() })
export type StepControlInput = z.infer<typeof StepControlInput>

export interface OpsSwitchView { scope: string; paused: boolean; reason: string | null }
export interface OpsSwitchesView { platform: OpsSwitchView; region: OpsSwitchView; source: 'control' | 'mirror' | 'unreachable' }
export const OpsSwitchInput = z.object({
  scope: z.string().regex(/^(platform|region:[a-z]+)$/),
  paused: z.boolean(),
  reason: z.string().trim().max(300).nullish(),
}).refine((v) => !v.paused || (v.reason ?? '').length >= 3, { message: 'Say why sending is being paused', path: ['reason'] })
export type OpsSwitchInput = z.infer<typeof OpsSwitchInput>
export interface IncidentView { id: string; region: string | null; company_id: string | null; kind: string; title: string; detail: string | null; severity: string; source: string; opened_at: string; resolved_at: string | null }
export interface OpsAlarmView {
  id: string; company_id: string | null; company_name: string | null; kind: string; subject: string; message: string; severity: string; status: string; opened_at: string
  /** Since when, how many, and example items to triage from (outcome and intake alarms). */
  detail?: AlarmDetail
  resolved_at?: string | null
  acknowledged_at?: string | null
  acknowledged_by?: string | null
  /** Reminders sent so far (the operator ladder) and when the next one is due. */
  notify_count?: number
  next_reminder_at?: string | null
}
/** What an alarm carries for triage (services/outcomes.ts, services/intake.ts). */
export interface AlarmDetail {
  since?: string | null
  affected?: number
  examples?: { kind: 'message' | 'event' | 'conversation' | 'exception'; id: string; url: string; label?: string }[]
  company_id?: string
  source_alarm_id?: string
  [k: string]: unknown
}

// ---------------------------------------------------------------------------
// Outcomes per channel (services/outcomes.ts)
// ---------------------------------------------------------------------------
export const OUTCOME_KEYS = ['answered', 'in_progress', 'set_aside', 'dismissed', 'spam', 'bounce', 'stuck_or_error'] as const
export type OutcomeKey = (typeof OUTCOME_KEYS)[number]
export const OUTCOME_LABELS: Record<OutcomeKey, string> = {
  answered: 'Answered', in_progress: 'Waiting or held', set_aside: 'Set aside by a rule', dismissed: 'Dismissed by a person',
  spam: 'Spam', bounce: 'Bounce', stuck_or_error: 'Stuck or failed',
}
export interface OutcomeWeekRow { week: string; counts: Record<OutcomeKey, number>; total: number; flags: Partial<Record<OutcomeKey, 'high' | 'low'>> }
export interface OutcomeChannelView { channel_id: string; channel: string; median: Record<OutcomeKey, number>; weeks: OutcomeWeekRow[] }
export interface StepSummaryRow { step: string; label: string; counts: Record<string, number>; reasons: { status: string; reason: string; n: number }[] }
export interface OutcomeAlarmView { id: string; kind: string; subject: string; message: string; severity: string; status: string; detail: AlarmDetail; opened_at: string; resolved_at: string | null }
export interface OutcomesView { channels: OutcomeChannelView[]; steps: StepSummaryRow[]; open: OutcomeAlarmView[]; history: OutcomeAlarmView[] }

/** One root cause on the Failed tab: an outage is one line, not hundreds of rows. */
export interface FailedGroupView { cause: string; class: 'outage' | 'message'; channel_id: string | null; channel: string | null; count: number; dead: number; retrying: number; first_at: string; last_at: string; example_id: string }
export const FailedGroupBulkInput = z.object({
  channel_id: z.uuid().nullish(),
  cause: z.string().max(200).nullish(),
  /** How many the person saw and confirmed: if the list changed meanwhile, nothing is done. */
  confirm: z.number().int().min(0).max(5000),
  note: z.string().max(300).nullish(),
})
/** A company's own dead letters (background work that gave up), for its admins. */
export interface CompanyDeadJobView { id: string; what: string; job_kind: string; attempts: number | null; dead_at: string }
/** Whether a channel is answered in takeit4me ("Not answered here" leaves it out of outcome and coverage checks). */
export const ChannelCoverageInput = z.object({ coverage: z.enum(['answered', 'not_answered']) })

// ---------------------------------------------------------------------------
// Operator alert settings: the per-kind matrix, the incident ladder, thresholds
// ---------------------------------------------------------------------------
/**
 * Company alarm kinds that are copied to takeit4me's operators (the per-type alert matrix: llm_failure,
 * smtp_failure, imap_failure, fatal_error, recovery_failed, max_retries_exhausted, the intake anomaly and the
 * outcome monitor). Operator-only kinds (model health, breakers, sweeps) are listed too so their channels can be set.
 */
export const OPERATOR_ALERT_KINDS: { kind: string; label: string; copied: boolean; min_severity: 'warning' | 'critical' }[] = [
  { kind: 'intake_anomaly', label: 'Intake anomaly (far fewer messages than usual)', copied: true, min_severity: 'critical' },
  { kind: 'silence', label: 'A channel has gone quiet', copied: true, min_severity: 'critical' },
  { kind: 'intake_dead', label: 'Incoming messages failed (fatal error)', copied: true, min_severity: 'critical' },
  { kind: 'dead_job', label: 'Dead letters (retries exhausted)', copied: true, min_severity: 'critical' },
  { kind: 'ai_failing', label: 'The AI could not answer (LLM failure)', copied: true, min_severity: 'critical' },
  { kind: 'send_failed', label: 'A channel cannot send (SMTP / SMS failure)', copied: true, min_severity: 'warning' },
  { kind: 'mailbox', label: 'A mailbox cannot be read (IMAP failure)', copied: true, min_severity: 'warning' },
  { kind: 'outcome_shift', label: 'Outcome anomalies (answer rate, set aside, spam, failures)', copied: true, min_severity: 'critical' },
  { kind: 'filtering', label: 'Mail being filtered', copied: true, min_severity: 'critical' },
  { kind: 'llm_health', label: 'Model health (failing or on a fallback)', copied: false, min_severity: 'critical' },
  { kind: 'provider', label: 'Provider circuit breakers', copied: false, min_severity: 'warning' },
  { kind: 'sweep_failing', label: 'Background steps failing', copied: false, min_severity: 'critical' },
  { kind: 'heartbeat', label: 'Scheduled jobs stopped', copied: false, min_severity: 'critical' },
]
export const OperatorAlertKind = z.object({
  /** Copy this kind of company alarm to operators (copied kinds only). */
  copy: z.boolean().default(true),
  /** The lowest severity copied or delivered. */
  min_severity: z.enum(['warning', 'critical']).optional(),
  email: z.boolean().default(true),
  webhook: z.boolean().default(true),
  /** A finding that cleared and came back within this many minutes is not paged again (0: always). */
  cooldown_minutes: z.number().int().min(0).max(24 * 60).default(0),
})
export const OperatorAlertSettings = z.object({
  kinds: z.record(z.string().regex(/^[a-z_]{2,40}$/), OperatorAlertKind).default({}),
  /** The incident ladder for critical operator alarms: minutes between reminders, then every `repeat_minutes` with no limit. */
  ladder_minutes: z.array(z.number().int().min(1).max(24 * 60)).min(1).max(8).default([15, 30, 60]),
  repeat_minutes: z.number().int().min(5).max(24 * 60).default(60),
  /** Intake anomaly per channel, per company and for the cell. */
  anomaly: z.object({
    window_hours: z.number().int().min(1).max(24).default(3),
    baseline_days: z.number().int().min(1).max(28).default(7),
    min_expected: z.number().min(1).max(10_000).default(5),
    drop_ratio: z.number().min(0.01).max(0.9).default(0.15),
  }).prefault({}),
  /** Model health. */
  llm: z.object({
    window_minutes: z.number().int().min(5).max(120).default(15),
    min_errors: z.number().int().min(1).max(1000).default(5),
    error_rate: z.number().min(0.05).max(1).default(0.5),
    min_fallback_calls: z.number().int().min(1).max(1000).default(3),
  }).prefault({}),
  /** Classification fallback: Jev down or not configured (never "unsure") on this share of messages. */
  jev: z.object({
    window_hours: z.number().int().min(1).max(168).default(24),
    min_messages: z.number().int().min(1).max(10_000).default(5),
    rate: z.number().min(0.01).max(1).default(0.2),
  }).prefault({}),
  /** Outcome checks per channel (services/outcomes.ts). Shares are 0..1; "points" are differences of shares. */
  outcomes: z.object({
    /** Absolute: over `absolute_days`, at least `absolute_min` real messages and under this share answered or waiting. */
    absolute_days: z.number().int().min(1).max(30).default(7),
    absolute_min: z.number().int().min(1).max(10_000).default(20),
    absolute_answer_rate: z.number().min(0.01).max(1).default(0.25),
    /** How far back "since" is looked for. */
    lookback_days: z.number().int().min(7).max(90).default(30),
    /** Shifts: the last `recent_days` against the `baseline_days` before them, with at least these many messages. */
    recent_days: z.number().int().min(1).max(14).default(3),
    baseline_days: z.number().int().min(7).max(90).default(28),
    recent_min: z.number().int().min(1).max(10_000).default(10),
    baseline_min: z.number().int().min(1).max(100_000).default(30),
    answered_drop: z.number().min(0.01).max(1).default(0.3),
    set_aside_rise: z.number().min(0.01).max(1).default(0.3),
    stuck_rise: z.number().min(0.01).max(1).default(0.1),
    spam_rise: z.number().min(0.01).max(1).default(0.3),
    dismissed_rise: z.number().min(0.01).max(1).default(0.4),
    /** One rule or failure stopping at least `block_min` messages in `block_days`; filters that routinely catch junk are left out. */
    block_days: z.number().int().min(1).max(30).default(7),
    block_min: z.number().int().min(1).max(10_000).default(20),
    junk_filters: z.array(z.string().max(40)).max(30).default(['loop', 'bounce', 'bulk', 'spam', 'dismissed']),
    /** On the page, a weekly share this far from the channel's median week is highlighted. */
    week_flag_points: z.number().min(0.01).max(1).default(0.15),
  }).prefault({}),
  /** Recovery: how long written-channel messages wait out a model outage, and the auto-reply window. */
  recovery: z.object({
    outage_wait_minutes: z.number().int().min(0).max(24 * 60).default(60),
    auto_reply_window_days: z.number().int().min(1).max(90).default(7),
  }).prefault({}),
})
export type OperatorAlertSettings = z.infer<typeof OperatorAlertSettings>
export interface ControlAuditView { id: string; at: string; actor: string; action: string; region: string | null; company_id: string | null; target: string | null; data: Record<string, unknown> }
export const OpsLifecycleInput = z.object({ to: CompanyStatus, reason: z.string().trim().min(3).max(300) })
export const OpsCreditInput = z.object({ delta: z.number().int().refine((n) => n !== 0, 'Must not be zero'), reason: z.string().trim().min(3).max(300) })

// ---------------------------------------------------------------------------
// Privacy (17.4-01): the terms and data processing agreement a company accepts at sign-up, and the
// published list of sub-processors with where they process data.
// ---------------------------------------------------------------------------
export const TERMS_VERSION = '2026-10-01'
export interface SubProcessor { name: string; purpose: string; data: string; location: string; regional: boolean }
export const SUBPROCESSORS: SubProcessor[] = [
  { name: 'Cloudflare', purpose: 'Hosting, routing, queues, Durable Objects, sandboxes, logs', data: 'All service data in transit; IDs only in queues; redacted logs', location: 'Placed in your home region; edge network worldwide', regional: true },
  { name: 'Supabase', purpose: 'Database, authentication and storage per region', data: 'All customer data at rest', location: 'Your home region (Sydney for Australia)', regional: true },
  { name: 'OpenAI', purpose: 'Language models and phone calls (GPT-Live)', data: 'Message and call content for drafting and voice; not stored by the vendor (store: false)', location: 'United States, or a regional endpoint where offered', regional: false },
  { name: 'Anthropic', purpose: 'Language models (only if enabled)', data: 'Message content for drafting', location: 'United States', regional: false },
  { name: 'OpenRouter (TypeSafe Jev)', purpose: 'Classification decisions', data: 'Message text with personal identifiers removed', location: 'United States', regional: false },
  { name: 'Twilio', purpose: 'SMS and phone numbers', data: 'Phone numbers and message content', location: 'United States, with regional edges', regional: false },
  { name: 'Telnyx', purpose: 'SMS and phone numbers', data: 'Phone numbers and message content', location: 'United States', regional: false },
  { name: 'Postmark', purpose: 'Email sending and receiving', data: 'Email addresses and content', location: 'United States', regional: false },
  { name: 'Stripe', purpose: 'Payments and takeit4me billing', data: 'Billing contacts and payment details (never seen by takeit4me)', location: 'United States and Ireland', regional: false },
  { name: 'GitHub', purpose: 'Code changes for coding tasks', data: 'Your repositories, only for tasks you approve', location: 'United States', regional: false },
]
